/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarRail,
} from "@/components/ui/sidebar"
import { useLocation } from "react-router-dom"
import { routes } from "@/config/routes-config"
import { canSeeMenuItem, groupRoutesByModule } from "./sidebar/menu-visibility"
import type { MenuRoute, SidebarRoute } from "@/@types/tab-navigation"
import type { Assignment } from "@/@types/user"
import type { RoleEnum } from "@/enum/role"
import { cn } from "@/lib/utils"
import { useGroupsQuery } from "@/services/user"
import { useAuthData } from "@/context/auth-data-provider"
import { useSubstitutionGate } from "@/hooks/use-substitution-gate"
import { getUserFromLocalStorage } from "@/utils/getUserFromLocalStorage"
import { groupNamesMap } from "./group-names-map"
import { useState, useMemo, useEffect, type ComponentProps } from "react"
import SidebarMenuRoutes from "./sidebar/sidebar-menu-routes"
import SidebarSettingsRoutes from "./sidebar/sidebar-settings-routes"
import { SidebarFooterComponent } from "./sidebar/sidebar-footer"
import SidebarTopHeader from "./sidebar/sidebar-header"
import { StatusEnum } from "@/enum/status"
import { GroupsEnum } from "@/enum/groups"

const HERDED_GROUP: string = GroupsEnum.RETAGUARDA_DE_LOJA
const GATE_HINT = "Requer entrar novamente para ativar a substituição"
import type { UserPermissionGroup } from "@/@types/user"
import { ChevronRight } from "lucide-react"
import "./sidebar.css"
import { Badge } from "components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { STORAGE_KEYS } from "@/constants/storage-keys"

const SIDEBAR_ROUTES: SidebarRoute[] = routes

const resolveItemPath = (item: any) => item.pathId + item.path.replace("/*", "")

export function AppSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
  const [openGroups, setOpenGroups] = useState<string[]>([])
  const [openCategories, setOpenCategories] = useState<string[]>([])

  const { data: groupsDefiner, isLoading: loadingGroups, refetch } = useGroupsQuery()
  const authData = useAuthData()
  const { guardedNavigate, isPendingRelogin } = useSubstitutionGate()
  // Fallback: se contexto não disponível (ex: HMR quebrou identidade), usar localStorage
  const cachedUser = useMemo(() => getUserFromLocalStorage(), [])
  const user = authData?.user ?? cachedUser

  const location = useLocation()

  const moduleAccessList = useMemo(() => {
    const perms = user?.user_permission_group || []
    return Array.isArray(perms) ? perms.map((gp: any) => gp.group) : []
  }, [user])

  useEffect(() => {
    const onGroupUpdated = () => refetch()
    window.addEventListener("group-updated", onGroupUpdated)
    return () => window.removeEventListener("group-updated", onGroupUpdated)
  }, [refetch])

  const groups = useMemo(() => {
    const perms = user?.user_permission_group || []
    return perms.filter((gp: any) => moduleAccessList.includes(gp.group))
  }, [user, moduleAccessList])

  const items_menu: SidebarRoute[] = useMemo((): SidebarRoute[] => {
    return SIDEBAR_ROUTES.filter((r: SidebarRoute) => r.groupRequired.some((g: string) => moduleAccessList.includes(g)))
  }, [moduleAccessList])

  const groupedByGroup: Record<string, Record<string, SidebarRoute[]>> = useMemo(
    (): Record<string, Record<string, SidebarRoute[]>> => groupRoutesByModule(items_menu, moduleAccessList),
    [items_menu, moduleAccessList],
  )

  const groupKeys = Object.keys(groupedByGroup)

  useEffect(() => {
    if (!loadingGroups && groupKeys.length === 0) {
      const t = setTimeout(() => refetch(), 1000)
      return () => clearTimeout(t)
    }
  }, [loadingGroups, groupKeys, refetch])

  const toggleGroup = (groupIndex: string) => {
    setOpenGroups((prev) => (prev.includes(groupIndex) ? prev.filter((g) => g !== groupIndex) : [...prev, groupIndex]))
  }

  const toggleCategory = (categoryKey: string) => {
    setOpenCategories((prev) =>
      prev.includes(categoryKey) ? prev.filter((c) => c !== categoryKey) : [...prev, categoryKey],
    )
  }

  const activeItemPath = useMemo(() => {
    const pathname = location.pathname

    return items_menu.reduce<string | null>((best, item: any) => {
      const itemPath = resolveItemPath(item)
      const matches = pathname === itemPath || pathname.startsWith(itemPath + "/")
      if (!matches) return best
      if (best === null || itemPath.length > best.length) return itemPath
      return best
    }, null)
  }, [items_menu, location.pathname])

  const isActiveItem = (item: any) => resolveItemPath(item) === activeItemPath

  const canSeeItem = (item: MenuRoute, currentRoles: RoleEnum[], assignments: Assignment[]): boolean =>
    canSeeMenuItem(item, { userGroups: moduleAccessList, userRole: currentRoles, assignments })

  const hasVisibleItemsInCategory = (items: any[], currentRoles: any[], assignments: any[]) => {
    return items.some((item) => canSeeItem(item, currentRoles, assignments))
  }

  const hasVisibleCategoriesInGroup = (group: string, currentRoles: any[], assignments: any[], grpDef: any) => {
    const categories = groupedByGroup[group]
    return Object.keys(categories).some((category) => {
      const catApi = grpDef?.categories?.find((c: any) => c.name === category)
      if (catApi?.status === StatusEnum.INACTIVE) return false
      return hasVisibleItemsInCategory(categories[category], currentRoles, assignments)
    })
  }

  return (
    <Sidebar {...props} className="bg-white border-r">
      <SidebarTopHeader />
      <SidebarContent className="bg-white p-3 overflow-y-auto overflow-x-hidden">
        <SidebarGroup>
          <SidebarMenuRoutes />
          <SidebarGroupLabel className="text-xs font-medium mb-2 mt-4">Grupos</SidebarGroupLabel>
          <SidebarGroupContent>
            {loadingGroups ? (
              <div className="space-y-2 mb-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="border-b border-gray-100 last:border-b-0 py-1">
                    <div className="flex items-center justify-between py-2 px-3">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-4 w-4 rounded-md" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-full space-y-1">
                {groupKeys.map((group, gi) => {
                  const grpDef = Array.isArray(groupsDefiner) ? groupsDefiner.find((g) => g.name === group) : undefined
                  const userGrp: UserPermissionGroup =
                    groups.find((g: any) => g.group === group) || ({} as UserPermissionGroup)
                  const { role: currentRoles = [], assignments = [] } = userGrp

                  // Não exibir grupo se não tiver nenhuma categoria/item visível
                  if (!hasVisibleCategoriesInGroup(group, currentRoles, assignments, grpDef)) {
                    return null
                  }

                  const groupKey = `group-${gi}`
                  const isGroupOpen = openGroups.includes(groupKey)

                  return (
                    <div key={gi} className="border-b border-gray-100 last:border-b-0">
                      <button
                        onClick={() => toggleGroup(groupKey)}
                        className={cn(
                          "w-full flex items-center justify-between py-2 px-3 text-xs font-medium rounded-lg transition-all duration-200",
                          "hover:bg-[#4B303C]/10 hover:text-gray-900 border border-transparent hover:border-[#4B303C]/20",
                          "focus:outline-none focus:ring-2 focus:ring-[#4B303C]/20",
                          isGroupOpen ? "bg-[#4B303C]/10 text-gray-900 border-[#4B303C]/20" : "text-gray-700",
                        )}
                      >
                        <span className="font-medium">{groupNamesMap[group] || group}</span>
                        <div
                          className={cn(
                            "transition-all duration-200 p-1 rounded-md",
                            isGroupOpen ? "rotate-90 bg-[#4B303C]/20" : "rotate-0 hover:bg-[#4B303C]/20",
                          )}
                        >
                          <ChevronRight className="h-3 w-3" />
                        </div>
                      </button>

                      <div
                        className={cn(
                          "grid transition-all duration-300 ease-in-out",
                          isGroupOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
                        )}
                      >
                        <div className="min-h-0 overflow-hidden">
                          <div className="pb-2 pt-1 ml-2">
                          <div className="space-y-1 border-l-2 border-gray-100 pl-3">
                            {Object.keys(groupedByGroup[group]).map((category, ci) => {
                              const catApi = grpDef?.categories?.find((c: any) => c.name === category)
                              if (catApi && catApi.status !== StatusEnum.ACTIVE) return null

                              if (category === group) {
                                return groupedByGroup[group][category].map((item, ii) =>
                                  canSeeItem(item, currentRoles, assignments) ? (
                                    <div
                                      key={item.id || ii}
                                      onClick={() => {
                                        if (item.onClick) {
                                          item.onClick()
                                          return
                                        }
                                        if (guardedNavigate(resolveItemPath(item), item.groupRequired)) {
                                          localStorage.setItem(STORAGE_KEYS.MODULE, group)
                                        }
                                      }}
                                      aria-disabled={(isPendingRelogin && item.groupRequired.includes(HERDED_GROUP)) || undefined}
                                      title={isPendingRelogin && item.groupRequired.includes(HERDED_GROUP) ? GATE_HINT : undefined}
                                      className={cn(
                                        "flex items-center gap-3 py-2 px-3 rounded-lg transition-all duration-200 cursor-pointer sidebar-menu-item",
                                        "text-xs hover:bg-[#4B303C] hover:text-white border border-transparent hover:border-[#4B303C]",
                                        isActiveItem(item)
                                          ? "bg-[#4B303C] text-white border-[#4B303C] shadow-sm"
                                          : "text-gray-700 hover:shadow-sm",
                                      )}
                                    >
                                      {item.icon && (
                                        <span className="transition-all duration-200 flex-shrink-0">
                                          <div className={cn(
                                            isActiveItem(item) ? "[&>svg]:text-white" : "[&>svg]:text-gray-500"
                                          )}>
                                            {item.icon}
                                          </div>
                                        </span>
                                      )}
                                      <span className="font-medium">{item.id}</span>
                                    </div>
                                  ) : null,
                                )
                              }

                              const categoryItems = groupedByGroup[group][category]
                              if (!hasVisibleItemsInCategory(categoryItems, currentRoles, assignments)) {
                                return null
                              }

                              const categoryKey = `${group}-${category}-${ci}`
                              const isCategoryOpen = openCategories.includes(categoryKey)

                              return (
                                <div key={ci} className="mt-2">
                                  <button
                                    onClick={() => toggleCategory(categoryKey)}
                                    className={cn(
                                      "w-full flex items-center justify-between py-2 px-3 text-xs font-medium rounded-lg transition-all duration-200",
                                      "hover:bg-[#4B303C]/10 hover:text-gray-900 border border-transparent hover:border-[#4B303C]/20",
                                      isCategoryOpen
                                        ? "bg-[#4B303C]/10 text-gray-900 border-[#4B303C]/20"
                                        : "text-gray-600",
                                    )}
                                  >
                                    <span className="font-medium">{category}</span>
                                    <div
                                      className={cn(
                                        "transition-all duration-200 p-0.5 rounded-md",
                                        isCategoryOpen ? "rotate-90 bg-[#4B303C]/20" : "rotate-0 hover:bg-[#4B303C]/20",
                                      )}
                                    >
                                      <ChevronRight className="h-2.5 w-2.5" />
                                    </div>
                                  </button>

                                  <div
                                    className={cn(
                                      "grid transition-all duration-300 ease-in-out",
                                      isCategoryOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
                                    )}
                                  >
                                    <div className="min-h-0 overflow-hidden">
                                      <div className="pt-2 pb-2 ml-3">
                                      <div className="space-y-1 border-l border-gray-200 pl-3">
                                        {groupedByGroup[group][category].map((item, ii) =>
                                          canSeeItem(item, currentRoles, assignments) ? (
                                            <div
                                              key={item.id || ii}
                                              onClick={() => {
                                                if (item.onClick) {
                                                  item.onClick()
                                                  return
                                                }
                                                if (guardedNavigate(resolveItemPath(item), item.groupRequired)) {
                                                  localStorage.setItem(STORAGE_KEYS.MODULE, group)
                                                }
                                              }}
                                              aria-disabled={(isPendingRelogin && item.groupRequired.includes(HERDED_GROUP)) || undefined}
                                              title={isPendingRelogin && item.groupRequired.includes(HERDED_GROUP) ? GATE_HINT : undefined}
                                              className={cn(
                                                "flex items-center gap-3 py-2 px-3 rounded-lg transition-all duration-200 cursor-pointer sidebar-menu-item",
                                                "text-xs hover:bg-[#4B303C] hover:text-white border border-transparent hover:border-[#4B303C]",
                                                isActiveItem(item)
                                                  ? "bg-[#4B303C] text-white border-[#4B303C] shadow-sm"
                                                  : "text-gray-600 hover:shadow-sm",
                                              )}
                                            >
                                              {item.icon && (
                                                <span className="transition-all duration-200 flex-shrink-0">
                                                  <div className={cn(
                                                    isActiveItem(item) ? "[&>svg]:text-white" : "[&>svg]:text-gray-400"
                                                  )}>
                                                    {item.icon}
                                                  </div>
                                                </span>
                                              )}
                                              <span className="font-medium">{item.id}</span>
                                              {
                                                item.isNew && (
                                                  <Badge className="text-[0.60rem] bg-[#2E0D1E] text-white hover:bg-[#2E0D1E]/80">Novo</Badge>
                                                )
                                              }
                                            </div>
                                          ) : null,
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
                })}
              </div>
            )}
          </SidebarGroupContent>
          <SidebarSettingsRoutes />
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooterComponent userInfo={user ?? undefined} />
      <SidebarRail />
    </Sidebar>
  )
}
